/**
 * 共通定数定義
 */

/**
 * 営業活動タイプ
 */
export type ActivityType = "電話" | "メッセ" | "訪問" | "DM";

/**
 * 営業活動タイプのマッピング
 * DB値 → 表示用ラベル
 */
export const ACTIVITY_TYPE_MAP = {
  phone: "電話",
  message: "メッセ",
  visit: "訪問",
  letter: "DM",
} as const satisfies Record<string, ActivityType>;

/**
 * 店舗名マッピング
 * 店舗ID → フル店舗名
 */
export const STORE_NAMES: Record<string, string> = {
  // Legacy numeric IDs
  "1": "櫻ロワイヤル",
  "2": "しらゆき",
  "3": "たまより",
  "4": "木花咲や",
  // Supabase UUIDs
  "50000001-0001-4001-8001-000000000001": "櫻ロワイヤル",
  "50000002-0002-4002-8002-000000000002": "しらゆき",
  "50000003-0003-4003-8003-000000000003": "たまより",
};

/**
 * 店舗名の短縮形
 * 店舗ID → 短縮店舗名
 */
export const STORE_SHORT_NAMES: Record<string, string> = {
  // Legacy numeric IDs
  "1": "櫻ロ",
  "2": "しら",
  "3": "たま",
  "4": "木花",
  // Supabase UUIDs
  "50000001-0001-4001-8001-000000000001": "櫻ロ",
  "50000002-0002-4002-8002-000000000002": "しら",
  "50000003-0003-4003-8003-000000000003": "たま",
};

/**
 * 曜日マッピング
 * 日曜日始まり (0 = 日, 1 = 月, ... , 6 = 土)
 */
export const DAY_OF_WEEK = ["日", "月", "火", "水", "木", "金", "土"] as const;

/**
 * ダッシュボード用の顧客タイプ (legacy)
 * 新コードでは HostessVisibleCustomerCategory を使うこと
 */
export type DashboardCustomerType =
  | "assigned"
  | "store"
  | "non";

/**
 * ダッシュボード用の顧客タイプごとの絵文字 (legacy)
 * 新コードでは HOSTESS_VISIBLE_CUSTOMER_CATEGORY_EMOJI を使うこと
 */
export const CUSTOMER_TYPE_EMOJI: Record<DashboardCustomerType, string> = {
  assigned: "🤵‍♂️",
  store: "🏛️",
  non: "👤",
};

/**
 * ダッシュボード用の顧客タイプごとのラベル (legacy)
 * 新コードでは HOSTESS_VISIBLE_CUSTOMER_CATEGORY_LABEL を使うこと
 */
export const CUSTOMER_TYPE_LABEL: Record<DashboardCustomerType, string> = {
  assigned: "担当",
  store: "店客",
  non: "非会員",
};

/**
 * ホステス視点での「表示すべき顧客カテゴリ」
 * RPC compute_hostess_visible_customer_category の戻り値と完全一致する単一情報源。
 * - assigned : 自分が assigned 担当
 * - branch   : 自分の assigned 担当が紹介した枝客
 * - store    : どこかの店舗で category='store' or 'assigned' (= 店客)
 * - non      : どこかの店舗で category='non' (= 非会員)
 * - others   : 上記いずれにも該当しない (例外)
 */
export type HostessVisibleCustomerCategory =
  | "assigned"
  | "branch"
  | "store"
  | "non"
  | "others";

/**
 * 顧客カテゴリ → 絵文字 (全画面共通の単一情報源)
 */
export const HOSTESS_VISIBLE_CUSTOMER_CATEGORY_EMOJI: Record<HostessVisibleCustomerCategory, string> = {
  assigned: "🤵‍♂️",
  branch: "🪾",
  store: "🏛️",
  non: "👤",
  others: "🙅",
};

/**
 * 顧客カテゴリ → 表示ラベル (全画面共通の単一情報源)
 */
export const HOSTESS_VISIBLE_CUSTOMER_CATEGORY_LABEL: Record<HostessVisibleCustomerCategory, string> = {
  assigned: "担当",
  branch: "枝客",
  store: "店客",
  non: "非会員",
  others: "他",
};

/**
 * 不明なカテゴリ値が来たときのフォールバック絵文字
 */
export const HOSTESS_VISIBLE_CUSTOMER_CATEGORY_FALLBACK_EMOJI = "👤";

/**
 * RPC 戻り値 (string) → 絵文字に安全に変換するヘルパー
 * 未知の値はフォールバックを返す
 */
export function toHostessVisibleCustomerCategoryEmoji(value: string | null | undefined): string {
  if (!value) return HOSTESS_VISIBLE_CUSTOMER_CATEGORY_FALLBACK_EMOJI;
  return (HOSTESS_VISIBLE_CUSTOMER_CATEGORY_EMOJI as Record<string, string>)[value]
    ?? HOSTESS_VISIBLE_CUSTOMER_CATEGORY_FALLBACK_EMOJI;
}

/**
 * 日付から曜日を取得する
 * @param dateStr - 日付文字列
 * @returns 曜日 (日, 月, 火, 水, 木, 金, 土)
 */
export function getDayOfWeek(dateStr: string): string {
  const date = new Date(dateStr);
  return DAY_OF_WEEK[date.getDay()];
}
